import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet-async';

import { StyledTermsOfService } from './styles';
import { TextLinkColor } from '../../../components/atoms/text-link-color';
import { bemClassName } from '../../../utils/bem';
import { SUPPORT_EMAIL } from '../../../utils/constants';
import { canonical, ogImage, ogUrl } from '../../../utils/seo';

const b = bemClassName('terms-of-service-page');

const TermsOfServicePage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <StyledTermsOfService>
      <Helmet>
        <title>Terms of Service — Usage Agreement | Active Shape</title>
        <meta
          name="description"
          content="Review the Active Shape terms and conditions of service governing your use of our health and fitness platform."
        />
        {canonical('/terms-of-service') && <link rel="canonical" href={canonical('/terms-of-service')} />}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Terms of Service — Usage Agreement | Active Shape" />
        <meta
          property="og:description"
          content="Review the Active Shape terms and conditions of service governing your use of our health and fitness platform."
        />
        {ogImage() && <meta property="og:image" content={ogImage()} />}
        {ogUrl('/terms-of-service') && <meta property="og:url" content={ogUrl('/terms-of-service')} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service — Usage Agreement | Active Shape" />
        <meta
          name="twitter:description"
          content="Review the Active Shape terms and conditions of service governing your use of our health and fitness platform."
        />
        {ogImage() && <meta name="twitter:image" content={ogImage()} />}
      </Helmet>
      <div className={b()}>
        <div className={b('card')}>
          <h1 className={b('title')}>Terms and Conditions of Service</h1>
          <p className={b('update')}>Last updated: December 13, 2024</p>

          <div className={b('nav-links')}>
            <TextLinkColor to="/privacy-policy">Privacy Policy</TextLinkColor>
            <TextLinkColor to="/user-data-deletion">Data Deletion Policy</TextLinkColor>
          </div>

          <p>
            Welcome to <strong>ActiveShape</strong>. By accessing and using our application, you agree to comply with
            these Terms and Conditions. Please read them carefully before using our services.
          </p>

          <h2 className={b('section-title')}>1. Acceptance of Terms</h2>
          <p>
            By registering, accessing, or using ActiveShape, you confirm that you have read, understood, and accepted
            these terms. If you do not agree with any part, you should not use the application.
          </p>

          <h2 className={b('section-title')}>2. Description of Service</h2>
          <p>ActiveShape is a health and wellness platform that offers:</p>
          <ul>
            <li>Personalized meal plans</li>
            <li>Healthy recipes tailored to your preferences</li>
            <li>Intermittent fasting tracking</li>
            <li>Health and fitness goal monitoring</li>
            <li>Recommendations based on your personal goals</li>
          </ul>

          <h2 className={b('section-title')}>3. Account Registration</h2>
          <p>To use ActiveShape, you must:</p>
          <ul>
            <li>Be at least 13 years old</li>
            <li>Provide accurate and up-to-date information</li>
            <li>Maintain the confidentiality of your credentials</li>
            <li>Notify us immediately of any unauthorized use</li>
          </ul>
          <p>You are responsible for all activities that occur under your account.</p>

          <h2 className={b('section-title')}>4. Acceptable Use</h2>
          <p>By using ActiveShape, you agree NOT to:</p>
          <ul>
            <li>Violate applicable laws or regulations</li>
            <li>Share false, misleading, or offensive content</li>
            <li>Attempt to access other users&apos; accounts</li>
            <li>Interfere with the platform&apos;s operation</li>
            <li>Use the application for unauthorized commercial purposes</li>
            <li>Reverse engineer or attempt to extract the source code</li>
          </ul>

          <h2 className={b('section-title')}>5. Content and Intellectual Property</h2>
          <p>
            All ActiveShape content, including but not limited to text, graphics, logos, icons, images, audio clips,
            recipes, and software, is the property of Tower Tech or its licensors and is protected by intellectual
            property laws.
          </p>
          <p>You may not copy, modify, distribute, or use our content without express written authorization.</p>

          <h2 className={b('section-title')}>6. Subscriptions and Payments</h2>
          <p>ActiveShape offers free and paid plans:</p>
          <ul>
            <li>Prices are displayed in the applicable local currency</li>
            <li>Subscriptions renew automatically</li>
            <li>You can cancel at any time from your profile</li>
            <li>Refunds are processed according to our refund policy</li>
          </ul>

          <h2 className={b('section-title')}>7. Referral Program</h2>
          <p>
            ActiveShape offers a Referral Program that allows users to earn commissions by referring new subscribers.
            Participation in the Referral Program is subject to separate{' '}
            <a href="/referral-terms">Referral Program Terms and Conditions</a>, which are incorporated into these Terms
            by reference. In the event of a conflict between these Terms and the Referral Program Terms, the Referral
            Program Terms shall govern with respect to the Referral Program.
          </p>

          <h2 className={b('section-title')}>8. Refund Policy</h2>
          <p>
            We offer refunds within the first 7 days of a new subscription if you are not satisfied with the service. To
            request a refund, contact our support team.
          </p>

          <h2 className={b('section-title')}>9. Limitation of Liability</h2>
          <p>
            <strong>Important:</strong> ActiveShape provides information and tools for general wellness. We are not
            medical professionals and our services do not substitute professional medical advice.
          </p>
          <ul>
            <li>Always consult with a health professional before starting any meal plan</li>
            <li>We are not responsible for decisions based on information from the app</li>
            <li>Use of the application is at your own risk</li>
          </ul>

          <h2 className={b('section-title')}>10. Service Availability</h2>
          <p>
            We strive to keep ActiveShape available 24/7, but we do not guarantee uninterrupted availability. We may
            temporarily suspend the service for maintenance or improvements without prior notice.
          </p>

          <h2 className={b('section-title')}>11. Modifications to Terms</h2>
          <p>
            We may update these terms occasionally. We will notify you of significant changes by email or through a
            notice in the application. Continued use after changes constitutes your acceptance.
          </p>

          <h2 className={b('section-title')}>12. Termination</h2>
          <p>
            We may suspend or terminate your access to ActiveShape if you violate these terms or if we detect suspicious
            activity. You can also delete your account at any time from settings.
          </p>

          <h2 className={b('section-title')}>13. Governing Law</h2>
          <p>
            These terms are governed by applicable laws. Any dispute will be resolved through arbitration or in the
            competent courts.
          </p>

          <h2 className={b('section-title')}>14. Contact</h2>
          <p>
            If you have questions about these Terms and Conditions, contact us at:{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>

          <div className={b('footer')}>
            <p>
              By using ActiveShape, you confirm that you have read and accepted these Terms and Conditions, as well as
              our Privacy Policy.
            </p>
            <div className={b('footer', 'links')}>
              <TextLinkColor to="/privacy-policy">Privacy Policy</TextLinkColor>
              <TextLinkColor to="/referral-terms">Referral Program Terms</TextLinkColor>
              <TextLinkColor to="/user-data-deletion">Data Deletion Policy</TextLinkColor>
            </div>
          </div>
        </div>
      </div>
    </StyledTermsOfService>
  );
};

export default TermsOfServicePage;
